<template>
    <div class="pdf-viewer">
        <SlideYDownTransition :duration="500">
            <header v-if="showHeader" ref="pdfHeader" class=" box-shadow d-flex justify-content-between pdf-toolbar">
                <PDFZoom :scale="scale" class="d-flex" @change="updateScale" @fit="updateFit" />
                <PDFPaginator v-model="currentPage" :page-count="pageCount" class="header-item" />
                <slot name="header"></slot>
            </header>
        </SlideYDownTransition>

        <PDFData
            class="pdf-viewer__main"
            :url="url"
            @page-count="updatePageCount"
            @page-focus="updateCurrentPage"
            @document-rendered="onDocumentRendered"
            @document-errored="onDocumentErrored"
        >
            <template v-slot:preview="{ pages }">
                <transition :name="isPreviewEnabled ? 'slide-right' : 'slide-left'">
                    <PDFPreview
                        v-show="isPreviewEnabled"
                        class="pdf-viewer__preview"
                        v-bind="{ pages, scale, currentPage, pageCount, isPreviewEnabled }"
                        @overlayTapped="togglePreview"
                    />
                </transition>
            </template>

            <template v-slot:document="{ pages }">
                <PDFDocument
                    class="pdf-viewer__document"
                    :class="{ 'preview-enabled': isPreviewEnabled }"
                    v-bind="{ pages, scale, optimalScale, fit, currentPage, pageCount, isPreviewEnabled }"
                    @scale-change="updateScale"
                />
            </template>
        </PDFData>
    </div>
</template>

<script>
import { SlideYDownTransition } from 'vue2-transitions';
import PDFDocument from './PDFDocument';
import PDFData from './PDFData';
import PDFPaginator from './PDFPaginator';
import PDFPreview from './PDFPreview';
import PDFZoom from './PDFZoom';

function floor(value, precision) {
    const multiplier = Math.pow(10, precision || 0);
    return Math.floor(value * multiplier) / multiplier;
}

export default {
    name: 'PDFViewer',

    components: {
        PDFDocument,
        PDFData,
        PDFPaginator,
        PDFPreview,
        PDFZoom,
        SlideYDownTransition,
    },

    props: {
        url: {
            type: String,
            default: '',
        },
        showHeader: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            scale: undefined,
            optimalScale: undefined,
            fit: undefined,
            currentPage: 1,
            pageCount: undefined,
            isPreviewEnabled: false,
        };
    },

    watch: {
        url() {
            this.currentPage = undefined;
        },
    },

    mounted() {
        document.body.classList.add('overflow-hidden');
    },

    methods: {
        onDocumentRendered() {
            this.$emit('document-errored', this.url);
        },

        onDocumentErrored(e) {
            this.$emit('document-errored', e);
        },

        updateScale({ scale, isOptimal = false }) {
            const roundedScale = floor(scale, 2);
            if (isOptimal) this.optimalScale = roundedScale;
            this.scale = roundedScale;
        },

        updateFit(fit) {
            this.fit = fit;
        },

        updatePageCount(pageCount) {
            this.pageCount = pageCount;
        },

        updateCurrentPage(pageNumber) {
            this.currentPage = pageNumber;
        },

        togglePreview() {
            this.isPreviewEnabled = !this.isPreviewEnabled;
        },
    },
};
</script>

<style scoped>
header {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    position: relative;
    z-index: 99;
}
.header-item {
    display: flex;
}

.pdf-viewer .pdf-viewer__document,
.pdf-viewer .pdf-viewer__preview {
    top: 0px;
    padding-top: 5rem;
}

.pdf-viewer__preview {
    display: block;
    width: 50%;
    max-width: 500px;
    right: 50%;
    background: lightslategrey;
    box-shadow: 0 0 10px #929292;
}

.pdf-viewer__document {
    top: 70px;
    width: 100%;
    left: 0;
}

@media print {
    header {
        display: none;
    }
}
</style>
